import { Flex } from "~/components/Flex";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { Fragment, ReactNode, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SetExceptionDialog } from "~/components/exceptions/SetExceptionDialog";
import {
  ComplianceFrameworkMutationAction,
  ComplianceFrameworkState,
  ControlState,
  DocumentType,
  ExceptionType,
  GetComplianceControlDocument,
  GetComplianceFrameworkControlsDocument,
  GetComplianceFrameworkDocument,
  GetComplianceFrameworksDocument,
  GetListFrameworksDocument,
  ListFrameworksFilterState,
  useApplyFrameworkMutation,
} from "~/operations";
import { Space } from "~/lib/types";
import { Markdown } from "~/components/markdown";
import { ComplianceControlData } from "~/pages/compliance/Control/ComplianceControl";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { mapSelectedEntitiesToString } from "~/components/exceptions/utils/mapSelectedEntitiesToString";
import { enqueueSnackbar } from "notistack";
import { getError } from "~/lib/handle-error";
import { CircularIconButton } from "~/components/CircularIconButton/CircularIconButton";
import { IconButtonType } from "~/components/CircularIconButton/DynamicButtonIcon";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import { isOfficialFramework } from "~/lib/frameworkUtils";
import { GenerateReportButton } from "~/components/GenerateReport";
import { FrameworkStateMenu } from "~/pages/compliance/Framework/FrameworkStateMenu";
import { FrameworkHeaderExceptionFlag } from "~/pages/compliance/Framework/FrameworkHeader/FrameworkHeaderExceptionFlag";
import {
  FrameworkIcon,
  useFrameworkIcon,
} from "~/pages/compliance/Framework/FrameworkIcon";
import { ComplianceFramework } from "../Framework";
import { useDialogs } from "@toolpad/core";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { OpenExceptions } from "~/components/app-search/OpenExceptions/OpenExceptions";

type Framework = {
  framework: ComplianceFramework;
  control?: never;
  exceptionGroups?: ExceptionGroup[];
};

type Control = {
  control: ComplianceControlData;
  framework?: never;
  exceptionGroups: ExceptionGroup[];
};

type Props = {
  space: Space;
  right?: ReactNode;
} & (Framework | Control);

export function FrameworkHeader({
  space,
  framework,
  control,
  exceptionGroups,
  right,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const { iconKey } = useFrameworkIcon({ framework });
  const [searchParams, _setSearchParams] = useSearchParams();
  const frameworkMrn = searchParams.get("frameworkMrn");
  let navigate = useNavigate();
  const dialogs = useDialogs();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const [applyFramework, { loading: loadingDelete }] =
    useApplyFrameworkMutation({
      refetchQueries: [
        // ComplianceFrameworkDocument and GetComplianceFrameworksDocument should
        // both be removed after isFeatureEnabled("Framework States") is removed and
        // the new framework flow is deployed
        {
          query: GetComplianceFrameworkDocument,
          variables: {
            input: {
              frameworkMrn,
              scopeMrn: space.mrn,
            },
          },
        },
        {
          query: GetComplianceFrameworksDocument,
          variables: {
            input: {
              scopeMrn: space.mrn,
            },
          },
        },
        {
          query: GetListFrameworksDocument,
          variables: {
            input: {
              scopeMrn: space.mrn,
              filterState: ListFrameworksFilterState.Active,
            },
          },
        },
      ],
    });

  const isOfficial = isOfficialFramework(framework?.mrn);

  const { handleRemoveExceptionGroups, handleSetException } = useExceptions({
    scopeMrns: [space.mrn],
    controlMrns: mapSelectedEntitiesToString([{ mrn: control?.mrn || "" }]),
    refetchQueries: [
      {
        query: GetComplianceFrameworkDocument,
        variables: {
          input: {
            frameworkMrn,
            scopeMrn: space.mrn,
          },
        },
      },
      {
        query: GetComplianceControlDocument,
        variables: {
          input: {
            controlMrn: control?.mrn,
            frameworkMrn,
            scopeMrn: space.mrn,
          },
        },
      },
      {
        query: GetComplianceFrameworkControlsDocument,
        variables: {
          input: {
            frameworkMrn,
            scopeMrn: space.mrn,
          },
          controlsInput: {
            after: null,
            limit: 50,
          },
        },
      },
    ],
  });

  // open and close the show more accordion when content is present
  const handleShowMore = () => {
    setExpanded((current) => !current);
  };

  // loop through the authors and create a string of names
  const authors = framework
    ? framework.authors?.map((author) => author.name).join(", ")
    : null;

  // get state of control or framework for powerSwitch
  const exceptionFlagState = framework ? framework.state : control.state;

  const handleFrameworkStateChange = async (
    state: ComplianceFrameworkMutationAction,
  ) => {
    if (!framework) return;

    try {
      await applyFramework({
        variables: {
          input: {
            action: state,
            frameworkMrn: framework.mrn,
            scopeMrn: space.mrn,
          },
        },
      });

      if (state === ComplianceFrameworkMutationAction.Disable) {
        navigate(`/space/compliance?spaceId=${space.id}`);
      }
    } catch (error) {
      const msg = getError(error);
      enqueueSnackbar(
        `Failed to change framework state ${msg ? ": " + msg : null}`,
        { variant: "error" },
      );
    }
  };

  // handle the powerswitch click when viewing a control
  // if currently active => open exceptions modal
  // if currently snoozed => toggle to active
  // if currently disabled => toggle to active
  const handleControlOptions = () => {
    if (!control) return;

    const action =
      control.state === ControlState.Active ? "openModal" : "toggleActive";

    switch (action) {
      case "openModal":
        dialogs.open(SetExceptionDialog, {
          onSave: handleSetException,
          target: "control",
          targetData: {
            targetTitle: control.title,
          },
        });
        break;
      case "toggleActive":
        handleRemoveExceptionGroups({
          exceptionMrns:
            exceptionGroups?.filter((eg) => eg.mrn).map((eg) => eg.mrn) ?? [],
        });
        break;
      default:
        return;
    }
  };

  const title = framework ? framework.name : control.title;
  const description = framework ? framework.description : control.description;
  // clip the description at 60 words
  const clippedDescription = description
    ? description.split(" ").slice(0, 60).join(" ")
    : "";
  // the rest of the clipped description if it exists
  const moreDescription = description
    ? description.split(" ").length > 60
      ? description.split(" ").slice(60).join(" ")
      : ""
    : "";

  const documentType = control
    ? DocumentType.ControlReport
    : DocumentType.FrameworkReport;

  return (
    <Fragment>
      <Grid container rowGap={3} columnSpacing={2} sx={{ mb: 3 }}>
        {framework?.state === ComplianceFrameworkState.Preview && (
          <Grid size={12}>
            <PreviewAlert />
          </Grid>
        )}

        <Grid
          sx={{ display: "flex", flexDirection: "column" }}
          size={{
            xs: 12,
            md: "grow",
          }}
        >
          <Flex alignItems="center">
            <Box sx={{ mr: iconKey ? 2 : 0 }}>
              <FrameworkIcon iconKey={iconKey} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              fontWeight={700}
              textTransform="uppercase"
            >
              {title}
            </Typography>
          </Flex>
          <Flex>
            {authors && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ mr: 3 }}
              >
                {authors}
              </Typography>
            )}
          </Flex>
        </Grid>
        <Grid
          sx={{ display: "flex", alignItems: "start", justifyContent: "end" }}
          size={{
            xs: 12,
            md: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {frameworkMrn && (
              <Fragment>
                {documentType === DocumentType.FrameworkReport && (
                  <GenerateReportButton
                    {...{
                      documentType: DocumentType.FrameworkReport,
                      frameworkMrn,
                      title,
                      space,
                    }}
                  />
                )}

                {documentType === DocumentType.ControlReport && control && (
                  <GenerateReportButton
                    {...{
                      documentType: DocumentType.ControlReport,
                      controlMrn: control.mrn,
                      frameworkMrn,
                      title,
                      space,
                    }}
                  />
                )}
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
              </Fragment>
            )}
            {framework ? (
              <>
                <Fragment>
                  <CircularIconButton
                    title={"Remove framework"}
                    iconType={IconButtonType.Delete}
                    sx={{ color: "error.main", mr: 2 }}
                    onClick={() => setDeleteDialogOpen(true)}
                  />
                  <FrameworkStateMenu
                    state={framework.state}
                    onStateChange={handleFrameworkStateChange}
                  />
                </Fragment>
              </>
            ) : (
              <FrameworkHeaderExceptionFlag
                state={exceptionFlagState}
                onClick={handleControlOptions}
              />
            )}
          </Box>
        </Grid>

        <Grid size={12}>
          <OpenExceptions
            scopeMrn={space.mrn}
            types={[ExceptionType.Compliance]}
            exceptionsGroups={exceptionGroups}
          />
        </Grid>

        {description.length > 0 && (
          <Grid size={{ xs: 12, md: right ? 8 : 12 }}>
            <Accordion
              expanded={expanded}
              disableGutters
              sx={{
                px: 0,
                background: "transparent",
                boxShadow: "none",
                "&:before": { display: "none" },
                "& .markdown": {
                  p: 0,
                  m: 0,
                  fontSize: (theme) => theme.spacing(2),
                  fontWeight: 600,
                  color: "text.secondary",
                  userSelect: "text",
                  cursor: "text",
                },
              }}
            >
              <AccordionSummary
                sx={{
                  px: 0,
                  m: 0,
                  "& .MuiAccordionSummary-content": { m: 0 },
                }}
              >
                <Markdown
                  className="markdown"
                  source={`${clippedDescription}${
                    moreDescription && !expanded && "..."
                  }`}
                />
              </AccordionSummary>
              <AccordionDetails
                sx={{ p: 0, m: 0, fontWeight: 600, color: "text.secondary" }}
              >
                {moreDescription && (
                  <Markdown className="markdown" source={moreDescription} />
                )}
              </AccordionDetails>
            </Accordion>
            {/* if there is more description content, view show more button */}
            {moreDescription && (
              <Button
                variant="text"
                onClick={handleShowMore}
                sx={{
                  px: 0,
                  py: 0.5,
                  mt: -0.75,
                  background: "transparent",
                  color: "secondary.light",
                  textTransform: "none",
                  "&:hover": { background: "transparent" },
                }}
              >
                Show {expanded ? "less" : "more"}
              </Button>
            )}
          </Grid>
        )}
        {right && <Grid size={{ xs: 12, sm: "auto", md: 4 }}>{right}</Grid>}
      </Grid>

      <ConfirmationDialog
        title={`${isOfficial ? "Remove" : "Delete"} framework ${framework?.name}?`}
        isOpen={deleteDialogOpen}
        confirmButtonText={isOfficial ? "Remove" : "Delete"}
        content={
          <Typography>
            {isOfficial
              ? "Are you sure you want to remove this framework?"
              : "Deleting a custom compliance framework will erase all data – this action cannot be undone. If you are facing problems, try replacing the file first.z"}
          </Typography>
        }
        onConfirm={() =>
          handleFrameworkStateChange(ComplianceFrameworkMutationAction.Disable)
        }
        onClose={() => setDeleteDialogOpen(false)}
        loading={loadingDelete}
      />
    </Fragment>
  );
}

const PreviewAlert = () => {
  return (
    <Alert variant="outlined" severity="info">
      <Typography fontWeight={500}>
        This compliance framework is in PREVIEW
      </Typography>
      <Typography variant="body2">
        You can see preliminary progress toward compliance with this framework,
        make configuration changes, and customize details.
      </Typography>
    </Alert>
  );
};
